// Эффекты оружия в мире: вспышки у дула (свет), трассеры и лучи, струя
// огнемёта, след ракеты, взрывы, звук. Частицы — общий ParticleSimulation
// (свои пресеты регистрируются здесь), свет — PointLight без «плафона».

import type { PointLight, Renderer } from "../engine/renderer";
import type { ParticleSimulation } from "../engine/particles/simulate";
import type { EmitterConfig } from "../engine/particles/types";
import { SfxLoop, playOneShot } from "../engine/sfx";
import { WeaponId, weaponDef } from "./catalog";

interface FxLight {
  x: number; y: number; z: number;
  range: number;
  color: [number, number, number];
  born: number; // мс
  life: number; // мс
}

function fireJetPreset(): EmitterConfig {
  return {
    visual: "fire", blend: "additive", rate: 0,
    lifetimeMin: 0.28, lifetimeMax: 0.5,
    speedMin: 0.4, speedMax: 1.6,
    coneAngleRad: 0.22, emitDir: [0, 0, 1],
    gravityZ: -1.2, drag: 1.4, turbulence: 2.4,
    baseSize: 0.5,
    sizeOverLife: [{ t: 0, v: 0.45 }, { t: 0.4, v: 1.3 }, { t: 1, v: 2.0 }],
    alphaOverLife: [{ t: 0, v: 1 }, { t: 0.7, v: 0.9 }, { t: 1, v: 0 }],
    offset: [0, 0, 0], enabled: false,
  };
}

function blastFirePreset(): EmitterConfig {
  return {
    visual: "fire", blend: "additive", rate: 0,
    lifetimeMin: 0.25, lifetimeMax: 0.6,
    speedMin: 2.5, speedMax: 9.0,
    coneAngleRad: Math.PI, emitDir: [0, 0, 1],
    gravityZ: -0.8, drag: 3.2, turbulence: 3.0,
    baseSize: 0.8,
    sizeOverLife: [{ t: 0, v: 0.5 }, { t: 0.3, v: 1.3 }, { t: 1, v: 0.4 }],
    alphaOverLife: [{ t: 0, v: 1 }, { t: 0.6, v: 0.9 }, { t: 1, v: 0 }],
    offset: [0, 0, 0], enabled: false,
  };
}

function blastSmokePreset(): EmitterConfig {
  return {
    visual: "smoke", blend: "alpha", rate: 0,
    lifetimeMin: 1.4, lifetimeMax: 2.8,
    speedMin: 0.6, speedMax: 3.0,
    coneAngleRad: Math.PI, emitDir: [0, 0, 1],
    gravityZ: -0.35, drag: 1.6, turbulence: 1.4,
    baseSize: 1.1,
    sizeOverLife: [{ t: 0, v: 0.4 }, { t: 0.4, v: 1.2 }, { t: 1, v: 2.0 }],
    alphaOverLife: [{ t: 0, v: 0 }, { t: 0.1, v: 0.55 }, { t: 0.7, v: 0.35 }, { t: 1, v: 0 }],
    offset: [0, 0, 0], enabled: false,
  };
}

function trailSmokePreset(): EmitterConfig {
  return {
    visual: "smoke", blend: "alpha", rate: 0,
    lifetimeMin: 0.6, lifetimeMax: 1.2,
    speedMin: 0.05, speedMax: 0.35,
    coneAngleRad: 1.2, emitDir: [0, 0, 1],
    gravityZ: -0.15, drag: 1.0, turbulence: 0.8,
    baseSize: 0.22,
    sizeOverLife: [{ t: 0, v: 0.5 }, { t: 1, v: 2.2 }],
    alphaOverLife: [{ t: 0, v: 0.55 }, { t: 1, v: 0 }],
    offset: [0, 0, 0], enabled: false,
  };
}

function sparkPreset(): EmitterConfig {
  return {
    visual: "spark", blend: "additive", rate: 0,
    lifetimeMin: 0.12, lifetimeMax: 0.35,
    speedMin: 2.0, speedMax: 7.5,
    coneAngleRad: 0.7, emitDir: [0, 0, 1],
    gravityZ: 9, drag: 1.0, turbulence: 0.3,
    baseSize: 0.07,
    sizeOverLife: [{ t: 0, v: 1 }, { t: 1, v: 0.2 }],
    alphaOverLife: [{ t: 0, v: 1 }, { t: 1, v: 0 }],
    offset: [0, 0, 0], enabled: false,
  };
}

/** Тракт звука одного непрерывного источника (пила, огнемёт). */
interface LoopSlot { loop: SfxLoop; lastMs: number; vol: number }

const MAX_LIGHTS = 10;
const HEAR_FULL = 4;
const HEAR_MAX = 40;

export class WeaponEffects {
  private lights: FxLight[] = [];
  private fireId: number;
  private blastFireId: number;
  private blastSmokeId: number;
  private trailId: number;
  private sparkId: number;
  private loops = new Map<string, LoopSlot>();
  /** Тряска камеры (0..1), затухает. */
  shake = 0;

  constructor(
    private renderer: Renderer,
    private particles: ParticleSimulation,
    private listener: () => { x: number; y: number; z: number },
    private volume: () => number,
    private lite: () => boolean,
  ) {
    this.fireId = particles.registerEmitter(fireJetPreset(), { x: 0, y: 0, z: 0 });
    this.blastFireId = particles.registerEmitter(blastFirePreset(), { x: 0, y: 0, z: 0 });
    this.blastSmokeId = particles.registerEmitter(blastSmokePreset(), { x: 0, y: 0, z: 0 });
    this.trailId = particles.registerEmitter(trailSmokePreset(), { x: 0, y: 0, z: 0 });
    this.sparkId = particles.registerEmitter(sparkPreset(), { x: 0, y: 0, z: 0 });
  }

  private t(): number {
    return this.renderer.getTimeAccSec();
  }

  /** Громкость по расстоянию до слушателя (0 — не слышно). */
  distVol(x: number, y: number, z: number, base: number): number {
    const l = this.listener();
    const d = Math.hypot(x - l.x, y - l.y, (z - l.z) * 0.6);
    if (d >= HEAR_MAX) return 0;
    const k = d <= HEAR_FULL ? 1 : 1 - (d - HEAR_FULL) / (HEAR_MAX - HEAR_FULL);
    return base * k * k * this.volume();
  }

  playAt(url: string, x: number, y: number, z: number, base: number, rate = 1): void {
    const v = this.distVol(x, y, z, base);
    if (v > 0.01) playOneShot(url, v, rate * (0.96 + Math.random() * 0.08));
  }

  play2d(url: string, base: number, rate = 1): void {
    const v = base * this.volume();
    if (v > 0.01) playOneShot(url, v, rate);
  }

  /** Непрерывный звук (пила/огнемёт) источника key — держится, пока зовут.
   *  vol — итоговая громкость (уже с общей громкостью и расстоянием). */
  keepLoop(key: string, url: string, vol: number, rate = 1): void {
    let s = this.loops.get(key);
    if (!s) {
      s = { loop: new SfxLoop(url), lastMs: 0, vol: 0 };
      this.loops.set(key, s);
    }
    s.lastMs = performance.now();
    s.vol = vol;
    s.loop.set(vol, rate);
  }

  addLight(x: number, y: number, z: number, range: number, color: [number, number, number], lifeMs: number): void {
    this.lights.push({ x, y, z, range, color, born: performance.now(), life: lifeMs });
    if (this.lights.length > MAX_LIGHTS) this.lights.shift();
  }

  /** Вспышка у дула: свет + горсть искр по направлению выстрела (у своего
   *  оружия искр нет — у самой камеры они разлетаются на весь экран). */
  muzzle(w: number, x: number, y: number, z: number, dx: number, dy: number, dz: number, own = false): void {
    if (w === WeaponId.Fists || w === WeaponId.Grenade) return;
    if (w === WeaponId.Laser) {
      this.addLight(x, y, z, 3.2, [2.2, 0.35, 0.25], 70);
      return;
    }
    if (w === WeaponId.Flamethrower) {
      this.addLight(x + dx, y + dy, z + dz, 5, [2.4, 1.2, 0.35], 120);
      return;
    }
    if (w === WeaponId.Chainsaw) return;
    const big = w === WeaponId.Shotgun || w === WeaponId.Rocket || w === WeaponId.Sniper;
    this.addLight(x, y, z, big ? 6.5 : 4.5, big ? [2.6, 1.7, 0.6] : [2.0, 1.35, 0.4], big ? 140 : 100);
    if (!this.lite() && !own) {
      this.particles.emitBurst(this.sparkId, x, y, z, this.t(), big ? 10 : 5, [dx, dy, dz], 0.03);
    }
  }

  /** Трассер выстрела по виду оружия. */
  tracer(w: number, ox: number, oy: number, oz: number, hx: number, hy: number, hz: number, own: boolean): void {
    switch (w) {
      case WeaponId.Laser:
        this.renderer.spawnTracer(ox, oy, oz, hx, hy, hz, {
          color: [1.0, 0.18, 0.12], size: 0.035, life: 0.09, density: 26, skip: own ? 0.35 : 0.1,
        });
        if (!this.lite()) this.particles.emitBurst(this.sparkId, hx, hy, hz, this.t(), 3, [ox - hx, oy - hy, oz - hz], 0.05);
        this.addLight(hx, hy, hz, 2.2, [1.6, 0.25, 0.15], 80);
        return;
      case WeaponId.Shotgun:
        this.renderer.spawnTracer(ox, oy, oz, hx, hy, hz, {
          color: [1.0, 0.8, 0.4], size: 0.012, life: 0.07, density: 3, skip: 1.0,
        });
        return;
      case WeaponId.Sniper:
        this.renderer.spawnTracer(ox, oy, oz, hx, hy, hz, {
          color: [0.9, 0.95, 1.0], size: 0.018, life: 0.35, density: 5, skip: 1.0,
        });
        return;
      default:
        if (!own) this.renderer.spawnTracer(ox, oy, oz, hx, hy, hz);
    }
  }

  /** Плевок огнемёта: струя огня вдоль направления, с разлётом. */
  flameJet(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, key: string): void {
    // Струя сразу во всю длину (6 м): сгустки вдоль луча, конус расширяется
    // к концу, каждый летит дальше по ходу струи и гаснет.
    const n = this.lite() ? 6 : 14;
    const t = this.t();
    for (let i = 0; i < n; i++) {
      const u = Math.pow(Math.random(), 0.8);
      const dist = 0.3 + u * 5.4;
      const spread = 0.08 + u * 0.7;
      const px = ox + dx * dist + (Math.random() - 0.5) * spread;
      const py = oy + dy * dist + (Math.random() - 0.5) * spread;
      const pz = oz + dz * dist + (Math.random() - 0.5) * spread * 0.7;
      const sp = 3 + 4 * (1 - u);
      this.particles.emitBurst(this.fireId, px, py, pz, t, 1, [dx, dy, dz], 0.02, [dx * sp, dy * sp, dz * sp + 0.4]);
    }
    this.addLight(ox + dx * 2.5, oy + dy * 2.5, oz + dz * 2.5, 6, [2.2, 1.0, 0.3], 140);
    this.keepLoop(key, weaponDef(WeaponId.Flamethrower).sound, this.distVol(ox, oy, oz, 0.45));
  }

  /** Горящий актёр: язычки пламени по телу. */
  burning(x: number, y: number, feetZ: number): void {
    if (Math.random() > (this.lite() ? 0.25 : 0.6)) return;
    this.particles.emitBurst(this.fireId, x, y, feetZ + 0.4 + Math.random() * 1.2, this.t(), 1, [0, 0, 1], 0.35, [0, 0, 1.2]);
  }

  /** Пила режет: искры из-под шины. */
  sawSparks(x: number, y: number, z: number, dx: number, dy: number, dz: number): void {
    if (this.lite()) return;
    this.particles.emitBurst(this.sparkId, x, y, z, this.t(), 2, [-dx, -dy, 0.6 - dz], 0.04);
  }

  /** След ракеты. */
  rocketTrail(x: number, y: number, z: number): void {
    this.particles.emitBurst(this.trailId, x, y, z, this.t(), this.lite() ? 1 : 2, undefined, 0.05);
    this.particles.emitBurst(this.fireId, x, y, z, this.t(), 1, [0, 0, 1], 0.03);
  }

  /** Взрыв: огненный шар, дым, искры, вспышка, тряска, звук. */
  explosion(x: number, y: number, z: number, radius: number): void {
    const t = this.t();
    const lite = this.lite();
    this.particles.emitBurst(this.blastFireId, x, y, z, t, lite ? 30 : 80, undefined, radius * 0.25);
    this.particles.emitBurst(this.blastSmokeId, x, y, z + 0.2, t, lite ? 10 : 28, undefined, radius * 0.3);
    if (!lite) this.particles.emitBurst(this.sparkId, x, y, z, t, 40, [0, 0, 1], 0.2);
    this.addLight(x, y, z + 0.4, radius * 4.5, [4.0, 2.4, 0.9], 420);
    const l = this.listener();
    const d = Math.hypot(x - l.x, y - l.y, z - l.z);
    this.shake = Math.max(this.shake, Math.max(0, 1 - d / (radius * 6)));
    this.playAt("/sounds/weapons/explosion.wav", x, y, z, 0.9);
  }

  /** Кадр: гасим непрерывные звуки без подпитки, затухание тряски. */
  frame(dtSec: number): void {
    const now = performance.now();
    for (const [k, s] of this.loops) {
      if (now - s.lastMs > 140) {
        s.loop.stop();
        this.loops.delete(k);
      }
    }
    this.shake = Math.max(0, this.shake - dtSec * 2.2);
  }

  stopAll(): void {
    for (const s of this.loops.values()) s.loop.stop();
    this.loops.clear();
    this.lights.length = 0;
    this.shake = 0;
  }

  /** Свет эффектов в общий список ламп кадра. */
  appendLights(out: PointLight[]): void {
    const now = performance.now();
    let w = 0;
    for (const L of this.lights) {
      const u = (now - L.born) / L.life;
      if (u >= 1) continue;
      this.lights[w++] = L;
      const k = (1 - u) * (1 - u);
      out.push({
        x: L.x, y: L.y, z: L.z,
        range: L.range * (0.6 + 0.4 * k),
        color: [L.color[0] * k, L.color[1] * k, L.color[2] * k],
        noBulb: true,
      });
    }
    this.lights.length = w;
  }
}
